import type { ReactNode } from "react";

/** Minimal markdown renderer for incident bodies.
 *
 * Incident bodies are stored as `body_markdown` and were rendered as a
 * raw text node, so the public page displayed the source verbatim:
 *
 *     **Component:** `upstream-asana`
 *     **New state:** `partial_outage`
 *
 * ADR-011 rules out a client-side markdown library (supply-chain
 * surface on a page whose entire job is to stay up and be trusted), and
 * the RSS builder likewise refuses to run a real renderer in a Lambda.
 * So this handles the small subset incident bodies actually use -
 * `**bold**`, `` `code` ``, and line breaks - in about thirty lines.
 *
 * Everything is built as React elements. There is no
 * dangerouslySetInnerHTML anywhere, so React escapes every text node and
 * an admin-authored body cannot inject markup no matter what it
 * contains. That property is the reason to hand-roll rather than reach
 * for a renderer that emits HTML strings.
 *
 * Unmatched or malformed syntax renders as literal text rather than
 * throwing - a status page must never fail to display an incident
 * because its formatting was wrong.
 */

// Capturing group so String.split keeps the delimiters. Bounded to a
// single line so an unclosed marker cannot swallow the rest of the body.
const INLINE_TOKEN = /(\*\*[^*\n]+\*\*|`[^`\n]+`)/;

function renderInline(line: string, keyPrefix: string): ReactNode[] {
  return line
    .split(INLINE_TOKEN)
    .filter((token) => token !== "")
    .map((token, index) => {
      const key = `${keyPrefix}-${index}`;

      if (token.length > 4 && token.startsWith("**") && token.endsWith("**")) {
        return <strong key={key}>{token.slice(2, -2)}</strong>;
      }

      if (token.length > 2 && token.startsWith("`") && token.endsWith("`")) {
        return (
          <code
            key={key}
            className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-800"
          >
            {token.slice(1, -1)}
          </code>
        );
      }

      return <span key={key}>{token}</span>;
    });
}

export function Markdown({
  text,
  className = "",
}: {
  text: string | undefined;
  className?: string;
}) {
  const lines = (text ?? "").split("\n");

  return (
    <p className={`whitespace-pre-wrap ${className}`.trim()}>
      {lines.map((line, index) => (
        // Newlines are emitted as text so the container's pre-wrap keeps
        // the original spacing, including blank lines between blocks.
        <span key={`line-${index}`}>
          {renderInline(line, `l${index}`)}
          {index < lines.length - 1 ? "\n" : null}
        </span>
      ))}
    </p>
  );
}
